import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatBadgeModule } from '@angular/material/badge';
import { MenuConfiguration, MenuItem } from '@newgenus/common';
import { MOBILE_HOME_ITEM } from '../../shared/utils/mobile-nav.util';
import { SheetDragDismissDirective } from '@newgenus/angular-shared-components/directives';

export interface MobileMenuSheetData {
    /** The permission-filtered menu (from SecurityService.generateSubMenuFromUserAccess). */
    menu: MenuConfiguration;
    /** The id of the destination matching the current route (for active highlight). */
    currentItemId: string | null;
}

export type MobileMenuResult = { action: 'navigate'; item: MenuItem } | { action: 'home' } | { action: 'customise' };

interface MenuGroup {
    header: string;
    items: MenuItem[];
}

/**
 * The phone primary-navigation sheet — the touch-first replacement for the
 * collapsed desktop sidebar. Lists Home, then every permitted section as a group
 * of its leaf items, then a "Customise bottom bar" action. Picking a row dismisses
 * the sheet with a {@link MobileMenuResult}; the host (app.component) routes.
 */
@Component({
    selector: 'newgenus-mobile-menu-sheet',
    imports: [CommonModule, MatListModule, MatIconModule, MatDividerModule, MatBadgeModule, SheetDragDismissDirective],
    template: `
        <div class="ng-sheet-grab" ngSheetDrag aria-hidden="true"></div>
        <mat-action-list class="mobile-menu-sheet">
            <button mat-list-item (click)="goHome()" [class.active]="data.currentItemId === homeId">
                <mat-icon matListItemIcon>home</mat-icon>
                <span matListItemTitle>Home</span>
            </button>

            <ng-container *ngFor="let group of groups">
                <mat-divider></mat-divider>
                <div mat-subheader>{{ group.header }}</div>
                <button
                    mat-list-item
                    *ngFor="let item of group.items"
                    (click)="navigate(item)"
                    [class.active]="data.currentItemId === item.id"
                >
                    <img
                        *ngIf="item.svg; else menuIconTpl"
                        matListItemIcon
                        class="menu-svg-icon"
                        [attr.src]="'/assets/icons/' + item.svg"
                        alt=""
                    />
                    <ng-template #menuIconTpl>
                        <mat-icon
                            matListItemIcon
                            [matBadge]="item.count"
                            [matBadgeHidden]="!item.showCount || !item.count"
                            matBadgeColor="accent"
                            matBadgeSize="small"
                            >{{ item.icon || 'chevron_right' }}</mat-icon
                        >
                    </ng-template>
                    <span matListItemTitle>{{ item.name }}</span>
                </button>
            </ng-container>

            <mat-divider></mat-divider>
            <button mat-list-item class="customise-row" (click)="customise()">
                <mat-icon matListItemIcon>tune</mat-icon>
                <span matListItemTitle>Customise bottom bar</span>
            </button>
        </mat-action-list>
    `,
    styles: [
        `
            /* System-bar inset comes from the GLOBAL container rule
               (global-styles.scss, DESIGN.md §Sheets) — no self-padding. */
            .mobile-menu-sheet {
                max-height: 70vh;
                overflow-y: auto;
            }
            .mobile-menu-sheet .active {
                background: rgba(25, 118, 210, 0.1);
            }
            .mobile-menu-sheet .active span[matListItemTitle] {
                color: #1976d2;
                font-weight: 600;
            }
            .customise-row {
                color: rgba(0, 0, 0, 0.6);
            }
            .menu-svg-icon {
                width: 24px;
                height: 24px;
                object-fit: contain;
            }
        `,
    ],
})
export class MobileMenuSheetComponent {
    public readonly homeId = MOBILE_HOME_ITEM.id;
    public groups: MenuGroup[] = [];

    constructor(
        private ref: MatBottomSheetRef<MobileMenuSheetComponent, MobileMenuResult>,
        @Inject(MAT_BOTTOM_SHEET_DATA) public data: MobileMenuSheetData
    ) {
        this.groups = Object.entries(this.data.menu || {})
            .filter(([key]) => key !== 'HOME')
            .map(([, cfg]) => ({
                header: cfg.header,
                items: [...cfg.topTierMenuItems, ...cfg.middleTierMenuItems],
            }))
            .filter((g) => g.items.length > 0);
    }

    public navigate(item: MenuItem): void {
        this.ref.dismiss({ action: 'navigate', item });
    }

    public goHome(): void {
        this.ref.dismiss({ action: 'home' });
    }

    public customise(): void {
        this.ref.dismiss({ action: 'customise' });
    }
}
